import { useState, useEffect } from 'react';

interface User {
  id: string;
  name: string;
  email: string;
  type: string;
}

interface Course {
  id: string;
  title: string;
  description: string;
  teacher_id: string;
  start_date?: string;
  end_date?: string;
  schedule?: string;
  created_at: string;
}

interface Enrollment {
  id: string;
  course_id: string;
  student_id: string;
  enrolled_at: string;
  student?: User;
  course?: Course;
}

interface ScheduleDay {
  day: number; // 2-8 (Thứ 2 - Chủ nhật)
  startTime: string;
  endTime: string;
}

export default function CourseManagement() {
  const [activeTab, setActiveTab] = useState<'courses' | 'students'>('courses');
  const [courses, setCourses] = useState<Course[]>([]);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [students, setStudents] = useState<User[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  
  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [updating, setUpdating] = useState(false);
  const [editStudentIds, setEditStudentIds] = useState<string[]>([]);
  const [editStudentSearch, setEditStudentSearch] = useState('');
  
  // Delete confirmation states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCourse, setDeletingCourse] = useState<Course | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Student enrollment states
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [newEnrollments, setNewEnrollments] = useState<string[]>([]);

  // Schedule states
  const [scheduleDays, setScheduleDays] = useState<ScheduleDay[]>([]);
  const [editScheduleDays, setEditScheduleDays] = useState<ScheduleDay[]>([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    teacher_id: '',
    start_date: '',
    end_date: '',
    student_ids: [] as string[]
  });

  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    teacher_id: '',
    start_date: '',
    end_date: ''
  });

  const dayNames = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'Chủ nhật'];

  // Calculate course status based on dates
  const getCourseStatus = (course: Course): 'active' | 'upcoming' | 'completed' => {
    if (!course.start_date || !course.end_date) {
      return 'active'; // Default to active if no dates
    }

    const now = new Date();
    const startDate = new Date(course.start_date);
    const endDate = new Date(course.end_date);

    if (now < startDate) {
      return 'upcoming';
    } else if (now > endDate) {
      return 'completed';
    } else {
      return 'active';
    }
  };

  // Parse schedule string to ScheduleDay array
  const parseSchedule = (scheduleStr: string): ScheduleDay[] => {
    if (!scheduleStr) return [];
    
    try {
      // Format: "Thứ 2, 4, 6 (18:00 - 20:00)"
      const match = scheduleStr.match(/Thứ\s+([\d,\s]+)\s*\((\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})\)/);
      if (!match) return [];

      const days = match[1].split(',').map(d => parseInt(d.trim()));
      const startTime = match[2];
      const endTime = match[3];

      return days.map(day => ({
        day,
        startTime,
        endTime
      }));
    } catch (e) {
      console.error('Error parsing schedule:', e);
      return [];
    }
  };

  // Convert ScheduleDay array to schedule string
  const formatSchedule = (days: ScheduleDay[]): string => {
    if (days.length === 0) return '';

    // Group by same time
    const timeGroups: { [key: string]: number[] } = {};
    days.forEach(day => {
      const timeKey = `${day.startTime}-${day.endTime}`;
      if (!timeGroups[timeKey]) {
        timeGroups[timeKey] = [];
      }
      timeGroups[timeKey].push(day.day);
    });

    // Format each group
    const schedules = Object.entries(timeGroups).map(([timeKey, dayList]) => {
      const [startTime, endTime] = timeKey.split('-');
      const sortedDays = dayList.sort((a, b) => a - b);
      return `Thứ ${sortedDays.join(', ')} (${startTime} - ${endTime})`;
    });

    return schedules.join('; ');
  };

  const addScheduleDay = () => {
    setScheduleDays([...scheduleDays, { day: 2, startTime: '08:00', endTime: '10:00' }]);
  };

  const removeScheduleDay = (index: number) => {
    setScheduleDays(scheduleDays.filter((_, i) => i !== index));
  };

  const updateScheduleDay = (index: number, field: keyof ScheduleDay, value: number | string) => {
    const updated = [...scheduleDays];
    updated[index] = { ...updated[index], [field]: value };
    setScheduleDays(updated);
  };

  const addEditScheduleDay = () => {
    setEditScheduleDays([...editScheduleDays, { day: 2, startTime: '08:00', endTime: '10:00' }]);
  };

  const removeEditScheduleDay = (index: number) => {
    setEditScheduleDays(editScheduleDays.filter((_, i) => i !== index));
  };

  const updateEditScheduleDay = (index: number, field: keyof ScheduleDay, value: number | string) => {
    const updated = [...editScheduleDays];
    updated[index] = { ...updated[index], [field]: value };
    setEditScheduleDays(updated);
  };

  // Generate hour and minute options
  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  const parseTime = (timeStr: string) => {
    const [hour, minute] = timeStr.split(':');
    return { hour, minute };
  };

  const formatTime = (hour: string, minute: string) => {
    return `${hour}:${minute}`;
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      // Fetch courses
      const coursesResponse = await fetch(`${supabaseUrl}/functions/v1/get-courses`, {
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (coursesResponse.ok) {
        const coursesData = await coursesResponse.json();
        setCourses(Array.isArray(coursesData) ? coursesData : coursesData.courses || []);
      }

      // Fetch users
      const usersResponse = await fetch(`${supabaseUrl}/functions/v1/get-users`, {
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        const usersList = Array.isArray(usersData) ? usersData : usersData.users || [];
        
        setTeachers(usersList.filter((u: User) => u.type === 'teacher'));
        setStudents(usersList.filter((u: User) => u.type === 'student'));
      }

      // Fetch enrollments
      const enrollmentsResponse = await fetch(`${supabaseUrl}/functions/v1/get-enrollments`, {
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (enrollmentsResponse.ok) {
        const enrollmentsData = await enrollmentsResponse.json();
        setEnrollments(Array.isArray(enrollmentsData) ? enrollmentsData : enrollmentsData.enrollments || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Không thể tải dữ liệu. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.teacher_id) {
      setError('Vui lòng nhập tên khóa học và chọn giáo viên');
      return;
    }

    if (scheduleDays.length === 0) {
      setError('Vui lòng thêm ít nhất một lịch học');
      return;
    }

    setCreating(true);
    setError('');
    setSuccessMessage('');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const scheduleStr = formatSchedule(scheduleDays);

      const response = await fetch(`${supabaseUrl}/functions/v1/create-course`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          schedule: scheduleStr
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage('Tạo khóa học thành công!');
        setFormData({
          title: '',
          description: '',
          teacher_id: '',
          start_date: '',
          end_date: '',
          student_ids: []
        });
        setScheduleDays([]);
        setShowCreateForm(false);
        fetchData();
        
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(data.error || 'Không thể tạo khóa học');
      }
    } catch (err) {
      console.error('Error creating course:', err);
      setError('Không thể tạo khóa học. Vui lòng thử lại.');
    } finally {
      setCreating(false);
    }
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setEditFormData({
      title: course.title,
      description: course.description || '',
      teacher_id: course.teacher_id,
      start_date: course.start_date || '',
      end_date: course.end_date || ''
    });
    // Parse schedule
    const parsedSchedule = parseSchedule(course.schedule || '');
    setEditScheduleDays(parsedSchedule.length > 0 ? parsedSchedule : [{ day: 2, startTime: '08:00', endTime: '10:00' }]);
    
    // Set current enrolled students
    const enrolledIds = getEnrolledStudentIds(course.id);
    setEditStudentIds(enrolledIds);
    setEditStudentSearch('');
    setShowEditModal(true);
    setError('');
  };

  const handleUpdateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editFormData.title || !editFormData.teacher_id || !editingCourse) {
      setError('Vui lòng nhập tên khóa học và chọn giáo viên');
      return;
    }

    if (editScheduleDays.length === 0) {
      setError('Vui lòng thêm ít nhất một lịch học');
      return;
    }

    setUpdating(true);
    setError('');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const scheduleStr = formatSchedule(editScheduleDays);

      // Update course info
      const response = await fetch(`${supabaseUrl}/functions/v1/update-course`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          courseId: editingCourse.id,
          ...editFormData,
          schedule: scheduleStr
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Không thể cập nhật khóa học');
      }

      // Update enrollments using the new admin-enroll-students function
      const enrollResponse = await fetch(`${supabaseUrl}/functions/v1/admin-enroll-students`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          courseId: editingCourse.id,
          studentIds: editStudentIds
        })
      });

      const enrollData = await enrollResponse.json();

      if (!enrollResponse.ok || !enrollData.success) {
        throw new Error(enrollData.error || 'Không thể cập nhật danh sách học sinh');
      }

      setSuccessMessage('Cập nhật khóa học thành công!');
      setShowEditModal(false);
      setEditingCourse(null);
      setEditStudentIds([]);
      setEditStudentSearch('');
      setEditScheduleDays([]);
      fetchData();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating course:', err);
      setError('Không thể cập nhật khóa học. Vui lòng thử lại.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteCourse = (course: Course) => {
    setDeletingCourse(course);
    setShowDeleteModal(true);
    setError('');
  };

  const confirmDeleteCourse = async () => {
    if (!deletingCourse) return;

    setDeleting(true);
    setError('');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/delete-course`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          courseId: deletingCourse.id
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage('Xóa khóa học thành công!');
        setShowDeleteModal(false);
        setDeletingCourse(null);
        fetchData();
        
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(data.error || 'Không thể xóa khóa học');
      }
    } catch (err) {
      console.error('Error deleting course:', err);
      setError('Không thể xóa khóa học. Vui lòng thử lại.');
    } finally {
      setDeleting(false);
    }
  };

  const handleEnrollStudents = async () => {
    if (!selectedCourse || newEnrollments.length === 0) return;

    setEnrolling(true);
    setError('');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const promises = newEnrollments.map(studentId =>
        fetch(`${supabaseUrl}/functions/v1/enroll-course`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            courseId: selectedCourse.id,
            studentId
          })
        })
      );

      await Promise.all(promises);
      
      setSuccessMessage(`Đã thêm ${newEnrollments.length} học sinh vào khóa học!`);
      setShowEnrollModal(false);
      setSelectedCourse(null);
      setNewEnrollments([]);
      fetchData();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error enrolling students:', err);
      setError('Không thể thêm học sinh vào khóa học. Vui lòng thử lại.');
    } finally {
      setEnrolling(false);
    }
  };

  const toggleEditStudent = (studentId: string) => {
    setEditStudentIds(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleStudent = (studentId: string) => {
    setFormData(prev => ({
      ...prev,
      student_ids: prev.student_ids.includes(studentId)
        ? prev.student_ids.filter(id => id !== studentId)
        : [...prev.student_ids, studentId]
    }));
  };

  const toggleNewEnrollment = (studentId: string) => {
    setNewEnrollments(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const getTeacherName = (teacherId: string) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? teacher.name : 'Chưa có giáo viên';
  };

  const getStudentName = (studentId: string) => {
    const student = students.find(s => s.id === studentId);
    return student ? student.name : 'Không tìm thấy';
  };

  const getStudentEmail = (studentId: string) => {
    const student = students.find(s => s.id === studentId);
    return student ? student.email : '';
  };

  const getCourseEnrollments = (courseId: string) => {
    return enrollments.filter(e => e.course_id === courseId);
  };

  const getEnrolledStudentIds = (courseId: string) => {
    return enrollments
      .filter(e => e.course_id === courseId)
      .map(e => e.student_id);
  };

  const getAvailableStudents = (courseId: string) => {
    const enrolledIds = getEnrolledStudentIds(courseId);
    return students.filter(s => !enrolledIds.includes(s.id));
  };

  // Filter students based on search query
  const filteredStudents = students.filter(student => 
    student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter students for edit modal
  const filteredEditStudents = students.filter(student => 
    student.name.toLowerCase().includes(editStudentSearch.toLowerCase()) ||
    student.email.toLowerCase().includes(editStudentSearch.toLowerCase())
  );

  // Filter enrollments for student list view
  const filteredEnrollments = enrollments.filter(enrollment => {
    const student = students.find(s => s.id === enrollment.student_id);
    const course = courses.find(c => c.id === enrollment.course_id);
    
    if (!student || !course) return false;
    
    const searchLower = studentSearchQuery.toLowerCase();
    return (
      student.name.toLowerCase().includes(searchLower) ||
      student.email.toLowerCase().includes(searchLower) ||
      course.title.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="flex items-center justify-center">
          <i className="ri-loader-4-line text-3xl text-teal-600 animate-spin"></i>
          <span className="ml-3 text-gray-600">Đang tải dữ liệu...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('courses')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'courses'
                ? 'bg-white text-teal-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <i className="ri-book-line mr-2"></i>
            Quản lý khóa học ({courses.length})
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'students'
                ? 'bg-white text-teal-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <i className="ri-user-line mr-2"></i>
            Danh sách học sinh ({enrollments.length})
          </button>
        </div>

        {activeTab === 'courses' && (
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center cursor-pointer"
          >
            <i className={`${showCreateForm ? 'ri-close-line' : 'ri-add-line'} mr-2`}></i>
            {showCreateForm ? 'Đóng' : 'Tạo khóa học mới'}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <i className="ri-error-warning-line text-red-600 text-xl mr-3"></i>
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <i className="ri-checkbox-circle-line text-green-600 text-xl mr-3"></i>
          <span className="text-green-700">{successMessage}</span>
        </div>
      )}

      {/* Courses Tab */}
      {activeTab === 'courses' && (
        <>
          {showCreateForm && (
            <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Tạo khóa học mới</h3>
              <form onSubmit={handleCreateCourse}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tên khóa học <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Nhập tên khóa học"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giáo viên <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.teacher_id}
                      onChange={(e) => setFormData({ ...formData, teacher_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                      required
                    >
                      <option value="">Chọn giáo viên</option>
                      {teachers.map(teacher => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.name} ({teacher.email})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ngày bắt đầu
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ngày kết thúc
                    </label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lịch học <span className="text-red-500">*</span>
                  </label>
                  
                  <div className="space-y-3">
                    {scheduleDays.map((schedule, index) => {
                      const startTime = parseTime(schedule.startTime);
                      const endTime = parseTime(schedule.endTime);
                      
                      return (
                        <div key={index} className="flex items-center gap-3 p-3 bg-white border border-gray-300 rounded-lg">
                          <div className="flex-1">
                            <select
                              value={schedule.day}
                              onChange={(e) => updateScheduleDay(index, 'day', parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                            >
                              {dayNames.map((name, idx) => (
                                <option key={idx} value={idx + 2}>
                                  {name}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {/* Start Time */}
                            <div className="flex items-center gap-1">
                              <select
                                value={startTime.hour}
                                onChange={(e) => updateScheduleDay(index, 'startTime', formatTime(e.target.value, startTime.minute))}
                                className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                              >
                                {hours.map(h => (
                                  <option key={h} value={h}>{h}</option>
                                ))}
                              </select>
                              <span className="text-gray-500">:</span>
                              <select
                                value={startTime.minute}
                                onChange={(e) => updateScheduleDay(index, 'startTime', formatTime(startTime.hour, e.target.value))}
                                className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                              >
                                {minutes.map(m => (
                                  <option key={m} value={m}>{m}</option>
                                ))}
                              </select>
                            </div>
                            
                            <span className="text-gray-500">-</span>
                            
                            {/* End Time */}
                            <div className="flex items-center gap-1">
                              <select
                                value={endTime.hour}
                                onChange={(e) => updateScheduleDay(index, 'endTime', formatTime(e.target.value, endTime.minute))}
                                className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                              >
                                {hours.map(h => (
                                  <option key={h} value={h}>{h}</option>
                                ))}
                              </select>
                              <span className="text-gray-500">:</span>
                              <select
                                value={endTime.minute}
                                onChange={(e) => updateScheduleDay(index, 'endTime', formatTime(endTime.hour, e.target.value))}
                                className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                              >
                                {minutes.map(m => (
                                  <option key={m} value={m}>{m}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeScheduleDay(index)}
                            className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            title="Xóa lịch học"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={addScheduleDay}
                    className="mt-3 px-4 py-2 text-teal-600 border border-teal-600 rounded-lg hover:bg-teal-50 transition-colors whitespace-nowrap flex items-center cursor-pointer"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Thêm lịch học
                  </button>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mô tả khóa học
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    rows={3}
                    placeholder="Nhập mô tả khóa học"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thêm học sinh vào khóa học
                  </label>
                  
                  {/* Search Input */}
                  <div className="mb-3 relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Tìm kiếm học sinh theo tên hoặc email..."
                      className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 w-5 h-5 flex items-center justify-center cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    )}
                  </div>

                  <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-white">
                    {students.length === 0 ? (
                      <p className="text-gray-500 text-sm">Chưa có học sinh nào</p>
                    ) : filteredStudents.length === 0 ? (
                      <div className="text-center py-4">
                        <i className="ri-search-line text-3xl text-gray-400 mb-2"></i>
                        <p className="text-gray-500 text-sm">Không tìm thấy học sinh phù hợp</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredStudents.map(student => (
                          <label key={student.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={formData.student_ids.includes(student.id)}
                              onChange={() => toggleStudent(student.id)}
                              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                            />
                            <span className="ml-3 text-sm text-gray-700">
                              {student.name} ({student.email})
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Đã chọn: {formData.student_ids.length} học sinh
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setScheduleDays([]);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center cursor-pointer"
                  >
                    {creating ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Đang tạo...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line mr-2"></i>
                        Tạo khóa học
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Courses List */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Tên khóa học
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Giáo viên
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Mô tả
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Thời gian
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Học sinh
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Trạng thái
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {courses.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-gray-500">
                        <i className="ri-book-line text-4xl mb-2"></i>
                        <p>Chưa có khóa học nào</p>
                      </td>
                    </tr>
                  ) : (
                    courses.map(course => (
                      <tr key={course.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-semibold text-gray-900">{course.title}</div>
                          <div className="text-sm text-gray-500">{course.description || 'Chưa có mô tả'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <i className="ri-user-line text-blue-600 text-sm"></i>
                            </div>
                            <span className="text-sm text-gray-900 font-medium">
                              {getTeacherName(course.teacher_id)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600 max-w-xs truncate">
                            {course.description || 'Chưa có mô tả'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600">
                            <div>{new Date(course.start_date).toLocaleDateString('vi-VN')}</div>
                            <div className="text-xs text-gray-500">
                              đến {new Date(course.end_date).toLocaleDateString('vi-VN')}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-semibold text-gray-900">
                              {getCourseEnrollments(course.id).length}
                            </span>
                            <span className="text-xs text-gray-500">học sinh</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            getCourseStatus(course) === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : getCourseStatus(course) === 'upcoming'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {getCourseStatus(course) === 'active' ? 'Đang diễn ra' : 
                             getCourseStatus(course) === 'upcoming' ? 'Sắp diễn ra' : 'Đã kết thúc'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => {
                                setSelectedCourse(course);
                                setNewEnrollments([]);
                                setShowEnrollModal(true);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                              title="Thêm học sinh"
                            >
                              <i className="ri-user-add-line text-lg"></i>
                            </button>
                            <button
                              onClick={() => handleEditCourse(course)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                              title="Chỉnh sửa"
                            >
                              <i className="ri-edit-line text-lg"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteCourse(course)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                              title="Xóa"
                            >
                              <i className="ri-delete-bin-line text-lg"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {courses.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-book-line text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">Không tìm thấy khóa học nào</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Students Tab */}
      {activeTab === 'students' && (
        <>
          {/* Search for students */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                value={studentSearchQuery}
                onChange={(e) => setStudentSearchQuery(e.target.value)}
                placeholder="Tìm kiếm theo tên học sinh, email hoặc tên khóa học..."
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              {studentSearchQuery && (
                <button
                  onClick={() => setStudentSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 w-5 h-5 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Học sinh</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Khóa học</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Giáo viên</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Ngày đăng ký</th>
                </tr>
              </thead>
              <tbody>
                {filteredEnrollments.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-500">
                      <i className="ri-user-line text-4xl mb-2"></i>
                      <p>
                        {studentSearchQuery 
                          ? 'Không tìm thấy kết quả phù hợp' 
                          : 'Chưa có học sinh nào đăng ký khóa học'
                        }
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredEnrollments.map(enrollment => {
                    const course = courses.find(c => c.id === enrollment.course_id);
                    return (
                      <tr key={enrollment.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                              <i className="ri-user-line text-blue-600 text-sm"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-800">
                              {getStudentName(enrollment.student_id)}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {getStudentEmail(enrollment.student_id)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-800 font-medium">
                          {course?.title || 'Không tìm thấy khóa học'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {course ? getTeacherName(course.teacher_id) : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(enrollment.enrolled_at).toLocaleDateString('vi-VN')}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Enroll Students Modal */}
      {showEnrollModal && selectedCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Thêm học sinh vào khóa học
              </h3>
              <button
                onClick={() => setShowEnrollModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Khóa học:</strong> {selectedCourse.title}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Đã có {getCourseEnrollments(selectedCourse.id).length} học sinh
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chọn học sinh chưa đăng ký
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-white">
                {getAvailableStudents(selectedCourse.id).length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">
                    Tất cả học sinh đã đăng ký khóa học này
                  </p>
                ) : (
                  <div className="space-y-2">
                    {getAvailableStudents(selectedCourse.id).map(student => (
                      <label key={student.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={newEnrollments.includes(student.id)}
                          onChange={() => toggleNewEnrollment(student.id)}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          {student.name} ({student.email})
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Đã chọn: {newEnrollments.length} học sinh
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowEnrollModal(false)}
                disabled={enrolling}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                onClick={handleEnrollStudents}
                disabled={enrolling || newEnrollments.length === 0}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center cursor-pointer"
              >
                {enrolling ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Đang thêm...
                  </>
                ) : (
                  <>
                    <i className="ri-user-add-line mr-2"></i>
                    Thêm học sinh
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Sửa khóa học</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditScheduleDays([]);
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <form onSubmit={handleUpdateCourse}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên khóa học <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData.title}
                    onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Nhập tên khóa học"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Giáo viên <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editFormData.teacher_id}
                    onChange={(e) => setEditFormData({ ...editFormData, teacher_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                    required
                  >
                    <option value="">Chọn giáo viên</option>
                    {teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name} ({teacher.email})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ngày bắt đầu
                  </label>
                  <input
                    type="date"
                    value={editFormData.start_date}
                    onChange={(e) => setEditFormData({ ...editFormData, start_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ngày kết thúc
                  </label>
                  <input
                    type="date"
                    value={editFormData.end_date}
                    onChange={(e) => setEditFormData({ ...editFormData, end_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lịch học <span className="text-red-500">*</span>
                </label>
                
                <div className="space-y-3">
                  {editScheduleDays.map((schedule, index) => {
                    const startTime = parseTime(schedule.startTime);
                    const endTime = parseTime(schedule.endTime);
                    
                    return (
                      <div key={index} className="flex items-center gap-3 p-3 bg-white border border-gray-300 rounded-lg">
                        <div className="flex-1">
                          <select
                            value={schedule.day}
                            onChange={(e) => updateEditScheduleDay(index, 'day', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                          >
                            {dayNames.map((name, idx) => (
                              <option key={idx} value={idx + 2}>
                                {name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Start Time */}
                          <div className="flex items-center gap-1">
                            <select
                              value={startTime.hour}
                              onChange={(e) => updateEditScheduleDay(index, 'startTime', formatTime(e.target.value, startTime.minute))}
                              className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                            >
                              {hours.map(h => (
                                <option key={h} value={h}>{h}</option>
                              ))}
                            </select>
                            <span className="text-gray-500">:</span>
                            <select
                              value={startTime.minute}
                              onChange={(e) => updateEditScheduleDay(index, 'startTime', formatTime(startTime.hour, e.target.value))}
                              className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                            >
                              {minutes.map(m => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                          </div>
                          
                          <span className="text-gray-500">-</span>
                          
                          {/* End Time */}
                          <div className="flex items-center gap-1">
                            <select
                              value={endTime.hour}
                              onChange={(e) => updateEditScheduleDay(index, 'endTime', formatTime(e.target.value, endTime.minute))}
                              className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                            >
                              {hours.map(h => (
                                <option key={h} value={h}>{h}</option>
                              ))}
                            </select>
                            <span className="text-gray-500">:</span>
                            <select
                              value={endTime.minute}
                              onChange={(e) => updateEditScheduleDay(index, 'endTime', formatTime(endTime.hour, e.target.value))}
                              className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                            >
                              {minutes.map(m => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeEditScheduleDay(index)}
                          className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                          title="Xóa lịch học"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={addEditScheduleDay}
                  className="mt-3 px-4 py-2 text-teal-600 border border-teal-600 rounded-lg hover:bg-teal-50 transition-colors whitespace-nowrap flex items-center cursor-pointer"
                >
                  <i className="ri-add-line mr-2"></i>
                  Thêm lịch học
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mô tả khóa học
                </label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  rows={3}
                  placeholder="Nhập mô tả khóa học"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quản lý học sinh trong khóa học
                </label>
                
                {/* Search Input */}
                <div className="mb-3 relative">
                  <input
                    type="text"
                    value={editStudentSearch}
                    onChange={(e) => setEditStudentSearch(e.target.value)}
                    placeholder="Tìm kiếm học sinh theo tên hoặc email..."
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  {editStudentSearch && (
                    <button
                      type="button"
                      onClick={() => setEditStudentSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 w-5 h-5 flex items-center justify-center cursor-pointer"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>

                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-white">
                  {students.length === 0 ? (
                    <p className="text-gray-500 text-sm">Chưa có học sinh nào</p>
                  ) : filteredEditStudents.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="ri-search-line text-3xl text-gray-400 mb-2"></i>
                      <p className="text-gray-500 text-sm">Không tìm thấy học sinh phù hợp</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredEditStudents.map(student => (
                        <label key={student.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={editStudentIds.includes(student.id)}
                            onChange={() => toggleEditStudent(student.id)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            {student.name} ({student.email})
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Đã chọn: {editStudentIds.length} học sinh
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditScheduleDays([]);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={updating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center cursor-pointer"
                >
                  {updating ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Đang cập nhật...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line mr-2"></i>
                      Cập nhật
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mr-4">
                <i className="ri-error-warning-line text-red-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Xác nhận xóa</h3>
                <p className="text-sm text-gray-600">Bạn có chắc chắn muốn xóa khóa học này?</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                <strong>Khóa học:</strong> {deletingCourse.title}
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Giáo viên:</strong> {getTeacherName(deletingCourse.teacher_id)}
              </p>
              <p className="text-xs text-red-600 mt-2">
                ⚠️ Hành động này sẽ xóa tất cả đăng ký liên quan và không thể hoàn tác!
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                onClick={confirmDeleteCourse}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center cursor-pointer"
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Đang xóa...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line mr-2"></i>
                    Xóa khóa học
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button
          onClick={fetchData}
          className="px-4 py-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors whitespace-nowrap flex items-center cursor-pointer"
        >
          <i className="ri-refresh-line mr-2"></i>
          Làm mới
        </button>
      </div>
    </div>
  );
}